import { useEffect, useMemo, useState } from 'react';
import { HeaderContent, HEADER_TITLE_CLASS } from '../components/shell/app-header';
import { Input } from '@/components/ui/input';
import { NativeSelect } from '@/components/ui/select';
import { api } from '../lib/api';

type RegistryEntry = Awaited<ReturnType<typeof api.admin.prompts.list.query>>[number];
type PromptVariable = RegistryEntry['variables'][number];
type VariableValue = boolean | string;

function defaultsFor(entry: RegistryEntry): Record<string, VariableValue> {
  const out: Record<string, VariableValue> = {};
  for (const v of entry.variables) out[v.name] = v.default;
  return out;
}

function VariableField({
  variable,
  value,
  onChange,
}: {
  variable: PromptVariable;
  value: VariableValue;
  onChange: (next: VariableValue) => void;
}) {
  if (variable.type === 'boolean') {
    return (
      <label className="flex items-center gap-2 text-[13px] font-bold text-foreground">
        <input
          type="checkbox"
          checked={value === true}
          onChange={(e) => onChange(e.target.checked)}
          className="size-4 accent-[var(--naia-blue)]"
        />
        <span>{variable.description || variable.name}</span>
      </label>
    );
  }
  return (
    <label className="flex flex-col gap-1 text-[13px] font-bold text-foreground">
      <span>{variable.description || variable.name}</span>
      {variable.options && variable.options.length > 0 ? (
        <NativeSelect value={String(value)} onChange={(e) => onChange(e.target.value)}>
          {variable.options.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </NativeSelect>
      ) : (
        <Input value={String(value)} onChange={(e) => onChange(e.target.value)} />
      )}
    </label>
  );
}

export default function AdminPrompts() {
  const [entries, setEntries] = useState<RegistryEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [variables, setVariables] = useState<Record<string, VariableValue>>({});
  const [rendered, setRendered] = useState<string | null>(null);
  const [renderError, setRenderError] = useState<string | null>(null);

  const selected = useMemo(
    () => entries?.find((e) => e.key === selectedKey) ?? null,
    [entries, selectedKey],
  );

  const visible = useMemo(() => {
    if (!entries) return [];
    const needle = filter.trim().toLowerCase();
    if (!needle) return entries;
    return entries.filter(
      (e) => e.key.toLowerCase().includes(needle) || e.description.toLowerCase().includes(needle),
    );
  }, [entries, filter]);

  useEffect(() => {
    let cancelled = false;
    api.admin.prompts
      .list
      .query()
      .then((data) => {
        if (!cancelled) setEntries(data);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!selectedKey) {
      setRendered(null);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      api.admin.prompts
        .render
        .query({ key: selectedKey, variables })
        .then((res) => {
          if (cancelled) return;
          setRendered(res.text);
          setRenderError(null);
        })
        .catch((e) => {
          if (cancelled) return;
          setRendered(null);
          setRenderError(e instanceof Error ? e.message : String(e));
        });
    }, 200);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [selectedKey, variables]);

  const select = (entry: RegistryEntry) => {
    setSelectedKey(entry.key);
    setVariables(defaultsFor(entry));
    setRendered(null);
    setRenderError(null);
  };

  return (
    <>
      <HeaderContent width="lg" showBurger backTo="/admin" backLabel="Admin">
        <h1 className={HEADER_TITLE_CLASS}>Prompts système</h1>
      </HeaderContent>
      <main className="max-w-[1100px] mx-auto mt-6 px-5 pb-16">
        <p className="text-muted-foreground mt-0 text-sm">
          Playground <strong>en lecture seule</strong>. Les prompts vivent dans le code
          (<code>transport/chat/prompts/</code>) ; cette page les rend depuis le registre pour
          prévisualiser l'effet des variables. Modifier un prompt, c'est ouvrir une PR sur le code.
        </p>

        {error && (
          <p role="alert" className="p-3 mb-4 rounded-md border-none font-bold text-sm bg-naia-info-bg text-foreground">
            {error}
          </p>
        )}

        {entries === null ? (
          <p className="text-muted-foreground">Chargement…</p>
        ) : (
          <div className="grid gap-5 md:grid-cols-[280px_1fr] items-start">
            <aside className="bg-card border border-border rounded-[16px] p-4 shadow-card">
              <Input
                placeholder="Filtrer les prompts…"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
              />
              <ul className="list-none p-0 m-0 mt-3 flex flex-col gap-1 max-h-[60vh] overflow-auto">
                {visible.map((entry) => (
                  <li key={entry.key}>
                    <button
                      type="button"
                      onClick={() => select(entry)}
                      className={
                        'w-full text-left rounded-[10px] px-3 py-2 border-none cursor-pointer ' +
                        (entry.key === selectedKey
                          ? 'bg-naia-blue text-card'
                          : 'bg-transparent text-foreground hover:bg-naia-info-bg')
                      }
                    >
                      <code className="text-[13px] font-extrabold">{entry.key}</code>
                      <span className="block text-[11.5px] opacity-80 mt-[2px]">{entry.description}</span>
                    </button>
                  </li>
                ))}
                {visible.length === 0 && <li className="text-muted-foreground text-sm">Aucun prompt.</li>}
              </ul>
            </aside>

            <section className="bg-card border border-border rounded-[16px] p-5 shadow-card min-w-0">
              {selected ? (
                <>
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <code className="text-[16px] font-extrabold">{selected.key}</code>
                    <span className="text-[13px] text-muted-foreground">{selected.description}</span>
                  </div>

                  {selected.variables.length > 0 && (
                    <div className="mt-4 flex flex-col gap-3">
                      {selected.variables.map((variable) => (
                        <VariableField
                          key={variable.name}
                          variable={variable}
                          value={variables[variable.name] ?? variable.default}
                          onChange={(next) =>
                            setVariables((prev) => ({ ...prev, [variable.name]: next }))
                          }
                        />
                      ))}
                    </div>
                  )}

                  {renderError && (
                    <p role="alert" className="p-3 mt-4 rounded-md border-none font-bold text-sm bg-naia-info-bg text-foreground">
                      {renderError}
                    </p>
                  )}

                  <pre className="mt-4 p-4 rounded-[12px] bg-naia-info-bg text-foreground text-[12.5px] leading-relaxed whitespace-pre-wrap break-words max-h-[60vh] overflow-auto">
                    {rendered ?? '…'}
                  </pre>
                </>
              ) : (
                <p className="text-muted-foreground text-sm">
                  Sélectionnez un prompt à gauche pour le prévisualiser.
                </p>
              )}
            </section>
          </div>
        )}
      </main>
    </>
  );
}
